'use es6';

import tinymce from 'tinymce';
import { BLOCK_FONT_SIZE_FORMAT, LINE_HEIGHT_FORMAT, FORMAT_COMMAND_TO_INHERITED_COMMAND, FONT_SIZE_BLOCKS, hsformattingConfig } from 'tinymce-plugins/constants/hsformatting';
import { overrideInheritedStyles, matchFont } from '../utils/formatting';
import { maybeGetComputedStyle } from '../utils/getComputedStyle';
import { getHsformattingSettings } from '../utils';
export const isFirstChild = (node, parentNode) => {
  const parent = parentNode || node.parentNode;
  return parent && node === parent.firstChild;
};
const isLastChild = (node, parentNode) => {
  const parent = parentNode || node.parentNode;
  return parent && node === parent.lastChild;
};
export const isNestedFirstChild = (node, parentNode) => {
  if (node === parentNode) return true;
  if (isFirstChild(node)) {
    return isNestedFirstChild(node.parentNode, parentNode);
  }
  return false;
};
export const isNestedLastChild = (node, parentNode) => {
  if (node === parentNode) return true;
  if (isLastChild(node)) {
    return isNestedLastChild(node.parentNode, parentNode);
  }
  return false;
};
export const isNestedOnlyChild = (node, parent) => {
  if (node === parent) return true;
  if (isFirstChild(node) && isLastChild(node)) {
    return isNestedOnlyChild(node.parentNode, parent);
  }
  return false;
};
export const isLinebreakNode = node => {
  if (node.nodeName === 'BR') return true;
  if (node.childNodes.length !== 1) return false;
  return isLinebreakNode(node.childNodes[0]);
};
function removeSpanStyleFromRange(editor, range, style) {
  const RangeUtils = tinymce.dom.RangeUtils;
  const rangeUtils = new RangeUtils(editor.dom);
  const nodesToProcess = [];
  const removeNakedSpans = node => {
    if (node.attributes.length === 1 && node.hasAttribute('style') && !node.getAttribute('style')) {
      editor.dom.remove(node, true);
    }
  };
  const maybeAddNode = node => {
    if (node.nodeName === 'SPAN' && editor.dom.getStyle(node, style)) {
      nodesToProcess.push(node);
    }
  };
  rangeUtils.walk(range, nodes => {
    nodes.forEach(node => {
      maybeAddNode(node);
      if (node.hasChildNodes()) {
        const spans = node.querySelectorAll('span[style]');
        if (spans.length) {
          Array.from(spans).forEach(maybeAddNode);
        }
      }
    });
  });
  if (nodesToProcess.length) {
    editor.dom.setStyle(nodesToProcess, style, null);
    nodesToProcess.forEach(removeNakedSpans);
  }
}
function getLineheightAsPercentage(value) {
  if (isNaN(value)) {
    return value;
  }
  return `${Math.round(Number(value) * 100)}%`;
}

/**
 * Get the allowlisted block element that starts the range if the range
 * starts at the beginning of the block
 *
 * @param {Object} editor Instance of a TinyMCE Editor
 * @param {Range} range Range object typically from a Selection
 * @param {Array} blocks array that provides a allowlist of possible node names
 *
 * @return {null|HTMLElement} a block element or null
 */
function getRangeStartBlock(editor, range, nodeAllowList) {
  // If the offset is not 0 we know that
  // the range isn't at the start
  if (range.startOffset !== 0 && !range.collapsed) {
    return null;
  }
  let start = range.startContainer;
  if (start.nodeType === Node.TEXT_NODE) {
    // If the parent node of a text node is the editor
    // body, we no that a content block is not range
    if (start.parentNode === editor.getBody()) {
      return null;
    }

    // If there's no parentNode, we don't have a valid range
    if (!start.parentNode) {
      return null;
    }

    // If the text node is not the first child of the
    // parent element, the range is not at the beginning
    // of a block
    if (start.parentNode.firstChild !== start) {
      return null;
    }

    // The selection begins at the first character of a text node,
    // so we can safely say that the start of the range is the
    // parent node
    start = start.parentNode;
  } else if (start.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  // If the start is a block, we know that the range is starting
  // at the beginning of the block
  if (nodeAllowList.includes(start.nodeName.toLowerCase())) {
    return start;
  }
  const ancestors = editor.$(start).parentsUntil(editor.getBody());
  if (!ancestors || ancestors.length === 0) {
    return null;
  }
  let child = start;
  let startBlock = null;

  // Search ancestors as long as the current elem is the first child
  // of the ancestor to determine if the range starts at block elem
  ancestors.each((index, elem) => {
    if (nodeAllowList.includes(elem.nodeName.toLowerCase()) && elem.firstChild === child) {
      startBlock = elem;
      return false;
    }
    child = elem;
    return true;
  });
  return startBlock;
}

/**
 * Get the allowlisted  block element that ends the range
 *
 * @param {Object} editor instance of a TinyMCE Editor
 * @param {Range} range range object typically from a Selection
 * @param {Array} nodeAllowList a allowlist of possible node names
 *
 * @return {null|HTMLElement} a block element or null
 */
function getRangeEndBlock(editor, range, nodeAllowList) {
  let end = range.endContainer;
  if (end.nodeType === Node.ELEMENT_NODE) {
    // The range must end after the last node
    // when the end container is an element node
    if (range.endOffset < end.childNodes.length) {
      return null;
    }
  } else if (end.nodeType === Node.TEXT_NODE) {
    // The range must end after the last character
    // the end container is a text node
    if (range.endOffset !== end.length) {
      return null;
    }

    // If the parent node is not a node in the TinyMCE editor
    // body we can assume that a block element in the content
    // is not the end of the selection
    if (end.parentNode === editor.getBody()) {
      return null;
    }

    // Verify that the text node is the last node in the parent
    if (end.parentNode.childNodes[end.parentNode.childNodes.length - 1] !== end) {
      return null;
    }

    // Since the text node is the last node, we can assume that the parent of the
    // text node is the end
    end = end.parentNode;
  } else {
    return null;
  }

  // If the current end node is a allowlisted node name then
  // the range ends at at the end of a block
  if (nodeAllowList.includes(end.nodeName.toLowerCase())) {
    return end;
  }
  const ancestors = editor.$(end).parentsUntil(editor.getBody());
  if (!ancestors || ancestors.length === 0) {
    return null;
  }
  let child = end;
  let endBlock = null;

  // Search ancestors as long as the current elem is the last child
  // of the ancestor to determine if the range ends at a
  // allowlisted block elem
  ancestors.each((index, elem) => {
    if (nodeAllowList.includes(elem.nodeName.toLowerCase()) && elem.childNodes[elem.childNodes.length - 1] === child) {
      endBlock = elem;
      return false;
    }
    child = elem;
    return true;
  });
  return endBlock;
}
function areEntireBlocksSelected(editor, nodeAllowList) {
  const range = editor.selection.getRng();
  const startBlock = getRangeStartBlock(editor, range, nodeAllowList);
  if (editor.selection.isCollapsed() && startBlock || startBlock && getRangeEndBlock(editor, range, nodeAllowList)) {
    return true;
  }
  return false;
}
export function applyLineHeight(editor, lineHeight) {
  const applyAsPercentage = getHsformattingSettings(editor.settings, hsformattingConfig.APPLY_LINE_HEIGHT_PERCENTAGE, false);
  if (applyAsPercentage) {
    lineHeight = getLineheightAsPercentage(lineHeight);
  }
  editor.undoManager.transact(() => {
    removeSpanStyleFromRange(editor, editor.selection.getRng(), 'line-height');
    const selectedNode = editor.selection.getNode();
    const isList = selectedNode.nodeName === 'UL' || selectedNode.nodeName === 'OL';
    editor.formatter.apply(LINE_HEIGHT_FORMAT, {
      value: lineHeight
    }, isList ? selectedNode : undefined);
  });
  return lineHeight;
}
export function applyFontSize(editor, fontSize) {
  const usePercentage = getHsformattingSettings(editor.settings, hsformattingConfig.APPLY_LINE_HEIGHT_PERCENTAGE, false);
  if (areEntireBlocksSelected(editor, FONT_SIZE_BLOCKS)) {
    // Default to a value that corresponds to the typical `normal` value
    let lineHeight = 1.25;
    if (usePercentage) {
      const node = editor.selection.getNode();
      if (node) {
        const computedStyle = maybeGetComputedStyle(editor.getWin(), node);
        if (computedStyle) {
          const currentLineHeight = parseFloat(computedStyle['line-height']);
          const currentFontSize = parseFloat(computedStyle['font-size']);
          if (!isNaN(currentLineHeight) && !isNaN(currentFontSize)) {
            lineHeight = currentLineHeight / currentFontSize;
          }
        }
      }
    }
    const startBlock = getRangeStartBlock(editor, editor.selection.getRng(), FONT_SIZE_BLOCKS);
    const endBlock = getRangeEndBlock(editor, editor.selection.getRng(), FONT_SIZE_BLOCKS) || startBlock;
    const range = editor.getDoc().createRange();

    // Create range that extends to cover the entire block so that parent spans are removed
    range.setStart(startBlock, 0);
    range.setEnd(endBlock, endBlock.childNodes.length);
    removeSpanStyleFromRange(editor, range, 'font-size');
    editor.formatter.apply(BLOCK_FONT_SIZE_FORMAT, {
      value: fontSize
    });
    if (usePercentage) {
      range.setEnd(endBlock, endBlock.childNodes.length);
      removeSpanStyleFromRange(editor, range, 'line-height');
      editor.formatter.apply(LINE_HEIGHT_FORMAT, {
        value: getLineheightAsPercentage(lineHeight)
      });
    }
    return;
  }
  editor.formatter.apply('fontsize', {
    value: fontSize
  });
}
export const applyFontStyles = (editor, styles) => {
  editor.formatter.remove(overrideInheritedStyles.bold.tinyMCECommand);
  editor.formatter.remove(overrideInheritedStyles.italic.tinyMCECommand);
  if (Object.keys(styles).length) {
    const target = editor.selection.getStart();
    editor.dom.setStyles(target, styles);
  }
};
export const shouldApplyOverrideFormat = (editor, overrideFormats = []) => {
  if (overrideFormats.length === 0) {
    return false;
  }
  if (areEntireBlocksSelected(editor, overrideFormats) || overrideFormats.includes(editor.selection.getNode().nodeName.toLowerCase())) {
    return true;
  }
  const selectedBlocks = editor.selection.getSelectedBlocks();
  const selector = overrideFormats.join(',');
  return !!selectedBlocks.find(element => overrideFormats.includes(element.nodeName.toLowerCase()) || !!element.querySelector(selector));
};
export function applyFormat(editor, format, overrideFormats, enabled) {
  if (overrideFormats.length > 0 && shouldApplyOverrideFormat(editor, overrideFormats)) {
    editor.undoManager.transact(() => {
      editor.formatter.remove(overrideInheritedStyles[format].tinyMCECommand);
      editor.formatter.remove(FORMAT_COMMAND_TO_INHERITED_COMMAND[format].on);
      let selectedNode = editor.selection.getNode();
      const parentAnchor = selectedNode.parentNode.tagName === 'A' && selectedNode.parentNode;
      if (parentAnchor && parentAnchor.firstChild === selectedNode && parentAnchor.lastChild === selectedNode) {
        // If the selection is a span that is the single child of an <a> we want to
        // apply the styles to <a> instead
        selectedNode = parentAnchor;
      }
      editor.formatter.apply(FORMAT_COMMAND_TO_INHERITED_COMMAND[format].on, {
        value: overrideInheritedStyles[format][enabled ? 'on' : 'off']
      }, selectedNode.tagName === 'A' ? selectedNode : undefined);
      if (!enabled) {
        //remove all formats applied to non-overridden elements
        editor.formatter.remove(FORMAT_COMMAND_TO_INHERITED_COMMAND[format].off);
      }
    });
    return;
  }
  editor.execCommand('mceToggleFormat', false, format);
}
export const computedStylesHasFont = (ele, editor, fonts) => {
  const computedStyle = maybeGetComputedStyle(editor.getWin(), ele);
  if (computedStyle) {
    const matchedfont = matchFont(computedStyle.fontFamily, fonts);
    if (matchedfont) {
      return true;
    }
  }
  return false;
};
export const getSelectedRngHasCustomFont = (editor, customFonts) => {
  if (!customFonts.length) {
    return false;
  }
  const selectedRng = editor.selection.getRng();
  const domIterator = document.createNodeIterator(selectedRng.commonAncestorContainer, NodeFilter.SHOW_ALL);
  let atStartContainer = true;
  while (domIterator.nextNode()) {
    let ele = domIterator.referenceNode;
    if (atStartContainer && ele !== selectedRng.startContainer) continue;
    atStartContainer = false;
    if (ele.nodeType === Node.TEXT_NODE) {
      ele = ele.parentElement;
    }
    if (computedStylesHasFont(ele, editor, customFonts)) {
      return true;
    }
    if (domIterator.referenceNode === selectedRng.endContainer) {
      break;
    }
  }
  return false;
};
export const clearRngContent = (editor, rng, startNode, endNode) => {
  rng.setStartBefore(startNode);
  rng.setEndAfter(endNode);
  editor.selection.setRng(rng);
  editor.selection.setContent('');
};
export const getContentOfSelection = (editor, rng, startNode, endNode) => {
  rng.setStartBefore(startNode);
  rng.setEndAfter(endNode);
  editor.selection.setRng(rng);
  return editor.selection.getContent();
};